import { defineMessages, useIntl } from 'react-intl';

import { DescriptionList } from '~/eds/src/components/DescriptionList/DescriptionList';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import {
  usePanelContext,
  useShouldPanelBeVisible,
} from '~/eds/src/components/Panel/PanelContext';
import { Text } from '~/eds/src/components/Text/Text';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import { TICKET_DETAILS_PANEL_WIDTH } from '../../constants';

const TASK_DETAILS_PANEL_ID_PREFIX = 'task-details';

export const getTaskDetailsPanelId = (taskId: string) =>
  `${TASK_DETAILS_PANEL_ID_PREFIX}-${taskId}`;

const messages = defineMessages({
  title: {
    id: 'TaskDetailsPanel.title',
    defaultMessage: 'Task Details',
    description: 'Title for the task details panel on ticket details page',
  },
  descriptionLabel: {
    id: 'TaskDetailsPanel.descriptionLabel',
    defaultMessage: 'Description',
    description: 'Label for the description field in task details panel',
  },
  dueDateLabel: {
    id: 'TaskDetailsPanel.dueDateLabel',
    defaultMessage: 'Due date',
    description: 'Label for the due date field in task details panel',
  },
  noDueDate: {
    id: 'TaskDetailsPanel.noDueDate',
    defaultMessage: '\u2014',
    description: 'Placeholder when no due date is set',
  },
  noDescription: {
    id: 'TaskDetailsPanel.noDescription',
    defaultMessage: '\u2014',
    description: 'Placeholder when no description is set',
  },
  errorMessage: {
    id: 'TaskDetailsPanel.errorMessage',
    defaultMessage: 'Unable to load task details. Please try again later.',
    description: 'Error message when task details fail to load',
  },
});

type Props = {
  taskId: string;
  panelId: string;
};

export const TaskDetailsPanel = ({ taskId, panelId }: Props) => {
  const { closePanel } = usePanelContext();
  const isVisible = useShouldPanelBeVisible(panelId);
  const intl = useIntl();
  const { navigate, params } = useRouting();
  const ticketId = params.ticketId!;
  const formatDate = useFormatDate();

  const {
    data: task,
    isLoading,
    isError,
  } = api.endpoints.getTaskK2.useQuery(
    { ticketId, taskId },
    { skip: !isVisible },
  );

  if (!isVisible) {
    return null;
  }

  const handleClose = () => {
    closePanel(panelId);
    const basePath = RoutePathType.WorkflowCLMEvolutionTicketsDetail.replace(
      ':ticketId',
      ticketId,
    );
    navigate(`${basePath}/tasks`, { replace: true });
  };

  return (
    <Panel
      mode="floating"
      position="right"
      width={TICKET_DETAILS_PANEL_WIDTH}
      hidden={{
        onHide: handleClose,
      }}
      loadingContent={{ isLoading }}
      placeholderContent={
        isError
          ? {
              icon: 'status-danger',
              message: intl.formatMessage(messages.errorMessage),
            }
          : undefined
      }
      title={intl.formatMessage(messages.title)}
    >
      {task && (
        <Layout direction="column" spacing={4}>
          <Text variant="section">{task.name}</Text>
          <DescriptionList
            terms={[
              {
                title: intl.formatMessage(messages.dueDateLabel),
                description: task.due_at
                  ? formatDate(task.due_at)
                  : intl.formatMessage(messages.noDueDate),
              },
              {
                title: intl.formatMessage(messages.descriptionLabel),
                description: task.description
                  ? task.description
                  : intl.formatMessage(messages.noDescription),
              },
            ]}
          />
        </Layout>
      )}
    </Panel>
  );
};
